import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { isEVMChain } from '../utils/crypto';
import { TBlockchain } from '../types';
import { safeConvertToNumber, MongoLong } from '../utils/number';

// Utility function to format Unix timestamp to YYYY-MM-DD
const formatFirstSeenDate = (
  timestamp: string | number | null | undefined,
): string | null => {
  if (!timestamp) return null;
  return new Date(Number(timestamp) * 1000).toISOString().split('T')[0];
};

// Base interface for common fields
interface BaseAddressStats {
  totalReceived: number;
  totalSent: number;
  txCount: number;
  firstSeen: string | null;
}

// Bitcoin-specific stats
export interface BitcoinAddressStats extends BaseAddressStats {
  chain: 'bitcoin';
}

// EVM-specific stats with additional fields
export interface EVMAddressStats extends BaseAddressStats {
  chain:
    | 'ethereum'
    | 'polygon'
    | 'avalanche'
    | 'bsc'
    | 'arbitrum'
    | 'optimism'
    | 'base';
  actualBalance: number;
  decimals: number;
  name: string;
  priceUsd: number;
  rawBalance: string;
  snapshotDate: string;
  symbol: string;
  tokenAddress: string;
  totalValueUsd: number;
}

// Discriminated union type
export type NormalizedAddressStats = BitcoinAddressStats | EVMAddressStats;

export interface NormalizedAddressSummary {
  summary:
    | {
        balance?: number;
        address_type?: string;
        id: string;
        entity?: string | null;
      } // Bitcoin
    | {
        id: string;
        balance?: number | MongoLong;
        [key: string]: unknown;
      }; // EVM + balance
}

// Hook for fast balance/summary data
export const useAddressData = (address: string, chain: TBlockchain) => {
  return useQuery({
    queryKey: ['addressData', address, chain],
    queryFn: async (): Promise<NormalizedAddressSummary> => {
      if (!address) throw new Error('Address is required');

      if (chain === 'bitcoin') {
        // Bitcoin: Just get the summary (fast)
        const summaryResult = await api.blockchain.getAccountSummary([address]);

        if (!summaryResult || typeof summaryResult !== 'object') {
          throw new Error('No data found for this Bitcoin address');
        }

        return {
          summary: summaryResult,
        };
      }

      if (isEVMChain(chain)) {
        // EVM: get native balance only
        const balanceResult = await api.blockchain.getEVMNativeBalance(
          chain,
          address,
        );
        const balanceData = balanceResult?.data?.[0];

        return {
          summary: {
            id: address,
            balance: balanceData?.raw_balance ?? 0, // raw_balance is in wei or MongoLong
          },
        };
      }

      throw new Error(`Unsupported chain: ${chain}`);
    },
    enabled: !!address && !!chain,
    retry: 1,
    staleTime: 0, // No caching
  });
};

// Separate hook for stats data (can be slower)
export const useAddressStats = (address: string, chain: TBlockchain) => {
  return useQuery({
    queryKey: ['addressStats', address, chain],
    queryFn: async (): Promise<NormalizedAddressStats | null> => {
      if (!address) throw new Error('Address is required');

      if (chain === 'bitcoin') {
        // Bitcoin: Get stats separately
        const statsResult =
          await api.blockchain.getBitcoinAddressSummaryStats(address);

        return {
          chain: 'bitcoin',
          totalReceived: statsResult.totalReceived ?? 0,
          totalSent: statsResult.totalSent ?? 0,
          txCount: statsResult.txCount ?? 0,
          firstSeen: formatFirstSeenDate(statsResult.firstSeen),
        } as BitcoinAddressStats;
      }

      if (isEVMChain(chain)) {
        // EVM: get balance data for stats
        const balanceResult = await api.blockchain.getEVMNativeBalance(
          chain,
          address,
        );
        const balanceData = balanceResult?.data?.[0];

        if (!balanceData) return null;

        return {
          chain: chain as EVMAddressStats['chain'],
          totalReceived: safeConvertToNumber(balanceData.total_incoming) / 1e18, // Convert wei to ETH
          totalSent: safeConvertToNumber(balanceData.total_outgoing) / 1e18, // Convert wei to ETH
          txCount: balanceData.total_number_of_transactions,
          firstSeen: formatFirstSeenDate(balanceData.firstSeen),
          actualBalance: balanceData.actual_balance,
          decimals: balanceData.decimals,
          name: balanceData.name,
          priceUsd: balanceData.price_usd,
          rawBalance: balanceData.raw_balance.toString(),
          snapshotDate: balanceData.snapshot_date,
          symbol: balanceData.symbol,
          tokenAddress: balanceData.token_address,
          totalValueUsd: balanceData.total_value_usd,
        } as EVMAddressStats;
      }

      throw new Error(`Unsupported chain: ${chain}`);
    },
    enabled: !!address && !!chain,
    retry: 1,
    staleTime: 0, // No caching
  });
};
